<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type {
  ContractManagementViewInDb,
  AndCriterionInput,
  AndCriterionOutput,
  ContractManagementViewUpdate,
  EnumResponseType,
} from '@/api';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import RecursiveQueryBuilder from './RecursiveQueryBuilder.vue';
import {
  validateFilters,
  normalizeFilterFromApi,
  type StaticCriterionColumnType,
} from '@/utils/contractManagementView/staticFilters';
import {
  type StaticColumnType,
  type EntityBasedStaticColumnType,
  STATIC_COLUMN_CONFIGS,
  isEntityBasedStaticColumn,
} from '@/types/contractManagement/viewTypes';
import type { ResponseType } from '@/utils/contractManagementView/filterValueValidation';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDrawer from '@/components/design/base/GaiaDrawer.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaTabs from '@/components/design/base/GaiaTabs.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import { logger } from '@/utils/logger';
import type { PreviewStaticColumnData } from '@/types/contractManagement/columnPreview';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  CONTRACT_ICON_STATES,
  contractStateLabel,
} from '@/components/design/domain/contractManagement/contractStateIcons';

export type { PreviewStaticColumnData };

interface Props {
  show: boolean;
  staticColumnType: StaticColumnType;
  viewData?: ContractManagementViewInDb | null;
  entityNameCache?: Record<EntityBasedStaticColumnType, Map<string, string>>;
  initialTab?: string;
  /** If true, works with local data instead of API */
  previewMode?: boolean;
  /** Static column config for preview mode */
  previewStaticColumn?: PreviewStaticColumnData | null;
  typeEnumResponseType?: EnumResponseType;
  tagsEnumResponseType?: EnumResponseType;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  /** Emitted after successful API update (persisted mode) */
  (e: 'updated'): void;
  /** Emitted with updated column data (preview mode) */
  (e: 'preview-updated', column: PreviewStaticColumnData): void;
}

const props = withDefaults(defineProps<Props>(), {
  viewData: null,
  entityNameCache: () => ({
    contractual_party: new Map(),
    matter: new Map(),
    owner: new Map(),
    effective_from: new Map(),
    effective_to: new Map(),
  }),
  initialTab: 'settings',
  previewMode: false,
  previewStaticColumn: null,
  typeEnumResponseType: undefined,
  tagsEnumResponseType: undefined,
});

const isPreviewMode = computed(() => props.previewMode);
const emit = defineEmits<Emits>();
const message = useGaiaToast();

const showDrawer = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const saving = ref(false);
const activeTab = ref<string>(props.initialTab || 'settings');

// Treat entity-based static columns as "static"; the rest as dynamic-style
const isEntityStaticColumn = computed(() => isEntityBasedStaticColumn(props.staticColumnType));

/**
 * Effective dates filter by the date value (ISO day), not by date-row uid, so
 * they build rules like any date column: a picker and the comparison operators.
 */
const isEffectiveDate = computed(
  () => props.staticColumnType === 'effective_from' || props.staticColumnType === 'effective_to'
);

/** Columns whose rules pick entity uids from `availableOptions`. */
const usesEntityPicker = computed(() => isEntityStaticColumn.value && !isEffectiveDate.value);

const staticResponseType = computed<ResponseType | undefined>(() => {
  switch (props.staticColumnType) {
    case 'created_at':
      return 'datetime';
    case 'effective_from':
    case 'effective_to':
      return 'date';
    case 'type':
      return 'str';
    case 'tags':
      return 'list[str]';
    case 'name':
    case 'state':
      return 'str';
    default:
      return undefined;
  }
});

/** Every state, worst first; the label rides along as the definition. */
const STATE_ENUM_RESPONSE_TYPE: EnumResponseType = {
  multiple_choices: false,
  values: CONTRACT_ICON_STATES.map(state => ({
    name: state,
    definition: contractStateLabel(state),
  })),
};

const staticEnumResponseType = computed<EnumResponseType | undefined>(() => {
  if (props.staticColumnType === 'type') return props.typeEnumResponseType;
  if (props.staticColumnType === 'tags') return props.tagsEnumResponseType;
  if (props.staticColumnType === 'state') return STATE_ENUM_RESPONSE_TYPE;
  return undefined;
});

const isStateColumn = computed(() => props.staticColumnType === 'state');

/**
 * The criterion type the column's filters use. Both date columns filter with the
 * one `date` criterion — the column, not the criterion, carries the date type.
 */
const criterionColumnType = computed<StaticCriterionColumnType | undefined>(() => {
  if (!isEntityStaticColumn.value) return undefined;
  const type = props.staticColumnType as EntityBasedStaticColumnType;
  return type === 'effective_from' || type === 'effective_to' ? 'date' : type;
});

// Only pass availableOptions for entity static columns
const availableOptionsForBuilder = computed(() =>
  usesEntityPicker.value ? availableOptions.value : []
);

// Get display name for static column type
/** Lower-case plural for the picker placeholder ("contractual parties", "matters"). */
const pluralDisplayName = computed(() => {
  const name = columnDisplayName.value.toLowerCase();
  return name.endsWith('y') ? `${name.slice(0, -1)}ies` : `${name}s`;
});

const columnDisplayName = computed(() => {
  switch (props.staticColumnType) {
    case 'contractual_party':
      return 'Contractual Party';
    case 'matter':
      return 'Matter';
    case 'owner':
      return 'Owner';
    case 'effective_from':
      return 'Effective from';
    case 'effective_to':
      return 'Effective to';
    case 'created_at':
      return 'Created At';
    case 'type':
      return 'Type';
    case 'tags':
      return 'Tags';
    case 'name':
      return 'Name';
    case 'state':
      return 'State';
    default:
      return 'Column';
  }
});

// Watch for prop changes to update active tab
watch(
  () => props.initialTab,
  newTab => {
    if (newTab) {
      activeTab.value = newTab;
    }
  }
);

// Name column has no Settings tab — always land on Filters
watch(
  () => [props.staticColumnType, props.show] as const,
  ([type, show]) => {
    if (show && type === 'name') {
      activeTab.value = 'filters';
    }
  },
  { immediate: true }
);

// Dynamic drawer width - wider when filters tab is active
const drawerWidth = computed((): number => {
  return activeTab.value === 'filters' ? 1000 : 700;
});

// Get column info from view data or preview data
const columnInfo = computed(() => {
  // In preview mode, use previewStaticColumn
  if (isPreviewMode.value) {
    if (props.previewStaticColumn) {
      return {
        name: props.previewStaticColumn.name,
        visible: props.previewStaticColumn.visible,
        order_index: props.previewStaticColumn.order_index,
      };
    }
    // Fallback for preview mode without previewStaticColumn
    return {
      name: columnDisplayName.value,
      visible: true,
      order_index: 0,
    };
  }

  // Persisted mode: use viewData
  if (!props.viewData) return null;

  // 'name' column has no nameKey/visibleKey/orderIndexKey — always visible, fixed position
  if (props.staticColumnType === 'name') {
    return {
      name: 'Name',
      visible: true,
      order_index: 0,
    };
  }

  const config = STATIC_COLUMN_CONFIGS[props.staticColumnType];

  return {
    name: props.viewData[config.nameKey] as string,
    visible: props.viewData[config.visibleKey] as boolean,
    order_index: props.viewData[config.orderIndexKey] as number,
  };
});

// Local state for editing
const localVisible = ref<boolean>(false);

// Filter state. The state column keeps two: `filters` for the contract
// state, `amendmentFilters` for the amendment state.
const filters = ref<AndCriterionInput | null>(null);
const amendmentFilters = ref<AndCriterionInput | null>(null);

/** The filters a view stores for a static column: [main, amendment (state column only)]. */
function storedFilters(
  view: ContractManagementViewInDb,
  type: StaticColumnType
): [AndCriterionOutput | null, AndCriterionOutput | null] {
  switch (type) {
    case 'contractual_party':
      return [view.contractual_party_filters, null];
    case 'matter':
      return [view.matter_filters, null];
    case 'owner':
      return [view.owner_filters, null];
    case 'effective_from':
      return [view.effective_from_filters, null];
    case 'effective_to':
      return [view.effective_to_filters, null];
    case 'created_at':
      return [view.created_at_filters, null];
    case 'type':
      return [view.document_type_filters, null];
    case 'tags':
      return [view.contract_tags_filters, null];
    case 'state':
      return [view.contract_state_filters, view.amendment_state_filters];
    case 'name':
      return [view.name_filters, null];
  }
}

/** Stored filters as the builder edits them; an empty AND is no filter. */
function toLocalFilters(stored: AndCriterionOutput | null): AndCriterionInput | null {
  if (!stored || stored.criteria.length === 0) return null;
  return normalizeFilterFromApi(stored) as AndCriterionInput | null;
}

/** Visibility and filters back to what the view (or the preview column) holds. */
function resetFromSource(): void {
  if (isPreviewMode.value) {
    localVisible.value = props.previewStaticColumn?.visible ?? true;
    filters.value = props.previewStaticColumn?.filters ?? null;
    amendmentFilters.value = props.previewStaticColumn?.amendmentFilters ?? null;
    return;
  }

  if (!props.viewData) {
    filters.value = null;
    amendmentFilters.value = null;
    localVisible.value = false;
    return;
  }

  // 'name' column has no STATIC_COLUMN_CONFIGS entry — it is always visible.
  localVisible.value =
    props.staticColumnType === 'name'
      ? true
      : (props.viewData[STATIC_COLUMN_CONFIGS[props.staticColumnType].visibleKey] as boolean);

  const [main, amendment] = storedFilters(props.viewData, props.staticColumnType);
  filters.value = toLocalFilters(main);
  amendmentFilters.value = toLocalFilters(amendment);
}

// Initialize filters and visibility from view data or preview data
watch(
  () => [
    props.viewData,
    props.previewStaticColumn,
    props.staticColumnType,
    props.show,
    props.previewMode,
  ],
  () => {
    if (!props.show) return;
    resetFromSource();
  },
  { immediate: true, deep: true }
);

// Get available options for the entity type (only for entity-based static columns)
const availableOptions = computed(() => {
  if (!isEntityStaticColumn.value) return [];

  const cache = props.entityNameCache[props.staticColumnType as EntityBasedStaticColumnType];

  return Array.from(cache.entries()).map(([uid, name]) => ({
    label: name,
    value: uid,
  }));
});

async function handleSave(): Promise<void> {
  // Validate filters before saving
  for (const candidate of [filters.value, amendmentFilters.value]) {
    if (!candidate) continue;
    const validation = validateFilters(candidate);
    if (!validation.valid) {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      message.error(`Invalid filter structure: ${validation.error || 'Unknown error'}`);
      return;
    }
  }

  if (isPreviewMode.value) {
    // Preview mode: emit updated data
    const updatedColumn: PreviewStaticColumnData = {
      type: props.staticColumnType,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
      name: props.previewStaticColumn?.name || columnDisplayName.value,
      visible: localVisible.value,
      order_index: props.previewStaticColumn?.order_index ?? 0,
      filters: filters.value,
      ...(isStateColumn.value ? { amendmentFilters: amendmentFilters.value } : {}),
    };

    emit('preview-updated', updatedColumn);
    message.success(`${columnDisplayName.value} column updated`);
    showDrawer.value = false;
    return;
  }

  // Persisted mode: save via API
  if (!props.viewData) return;

  const emptyFilters: AndCriterionInput = { criterion_type: 'and', criteria: [] };
  const filtersToSend: AndCriterionInput = filters.value ?? emptyFilters;

  saving.value = true;
  try {
    let updateData: ContractManagementViewUpdate;

    if (props.staticColumnType === 'name') {
      // 'name' column has no visible/order/name keys — only name_filters
      updateData = { name_filters: filtersToSend };
    } else {
      const config = STATIC_COLUMN_CONFIGS[props.staticColumnType];

      updateData = {
        [config.visibleKey]: localVisible.value,
      };

      switch (props.staticColumnType) {
        case 'contractual_party': {
          updateData.contractual_party_filters = filtersToSend;

          break;
        }
        case 'matter': {
          updateData.matter_filters = filtersToSend;

          break;
        }
        case 'owner': {
          updateData.owner_filters = filtersToSend;

          break;
        }
        case 'effective_from': {
          updateData.effective_from_filters = filtersToSend;

          break;
        }
        case 'effective_to': {
          updateData.effective_to_filters = filtersToSend;

          break;
        }
        case 'created_at': {
          updateData.created_at_filters = filtersToSend;

          break;
        }
        case 'type': {
          updateData.document_type_filters = filtersToSend;

          break;
        }
        case 'tags': {
          updateData.contract_tags_filters = filtersToSend;

          break;
        }
        case 'state': {
          updateData.contract_state_filters = filtersToSend;
          updateData.amendment_state_filters = amendmentFilters.value ?? emptyFilters;

          break;
        }
        // No default
      }
    }

    await ContractViewsService.update(props.viewData.uid, updateData);

    message.success(`${columnDisplayName.value} column updated successfully`);
    emit('updated');
    showDrawer.value = false;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Failed to update column';
    message.error(errorMsg);
    logger.error('Error updating static column filters:', error);
  } finally {
    saving.value = false;
  }
}

function handleCancel(): void {
  resetFromSource();
  showDrawer.value = false;
}
</script>

<template>
  <gaia-drawer
    :show="props.show"
    :width="drawerWidth"
    :title="`Edit ${columnDisplayName} column`"
    subtitle="Set whether this column shows in the grid, and the filters it applies."
    @update:show="(value: boolean) => (value ? undefined : handleCancel())"
  >
    <gaia-empty-state v-if="!columnInfo" dashed title="Loading column information…" />

    <gaia-tabs v-else v-model:value="activeTab" type="line">
      <n-tab-pane v-if="staticColumnType !== 'name'" name="settings" tab="Column settings">
        <n-flex vertical :size="16" :wrap="false" :style="{ paddingTop: '8px' }">
          <gaia-toggle-row
            card
            label="Visible"
            hint="Show this column in the grid"
            :checked="localVisible"
            @update:checked="(value: boolean) => (localVisible = value)"
          />

          <!-- A static column's name and position are fixed. They read as facts
               rather than as disabled inputs, which only invite a click. -->
          <div>
            <gaia-field-row variant="plain" label="Column name">
              {{ columnInfo.name || columnDisplayName }}
            </gaia-field-row>
            <gaia-field-row variant="plain" label="Order index" :divider="false">
              {{ columnInfo.order_index }}
            </gaia-field-row>
          </div>
        </n-flex>
      </n-tab-pane>

      <n-tab-pane name="filters" tab="Filters">
        <n-flex vertical :size="12" :wrap="false" :style="{ paddingTop: '8px' }">
          <gaia-text size="dense" tone="tertiary">
            Configure automatic filters for this column. Only rows matching these filters will be
            displayed.
          </gaia-text>
          <gaia-section-header v-if="isStateColumn">Contract state</gaia-section-header>
          <RecursiveQueryBuilder
            v-model="filters"
            :available-options="availableOptionsForBuilder"
            :placeholder="`Select ${pluralDisplayName}...`"
            :static-column-type="criterionColumnType"
            :is-static-column="usesEntityPicker"
            :allow-empty-date="isEffectiveDate"
            :response-type="staticResponseType"
            :enum-response-type="staticEnumResponseType"
          />
          <!-- The state column holds two states, so it filters by each. -->
          <template v-if="isStateColumn">
            <gaia-section-header>Amendment state</gaia-section-header>
            <RecursiveQueryBuilder
              v-model="amendmentFilters"
              :available-options="[]"
              :is-static-column="false"
              :response-type="staticResponseType"
              :enum-response-type="staticEnumResponseType"
            />
          </template>
        </n-flex>
      </n-tab-pane>
    </gaia-tabs>

    <template #footer>
      <n-flex justify="end" :size="10">
        <gaia-button @click="handleCancel">Cancel</gaia-button>
        <gaia-button type="primary" :loading="saving" @click="handleSave">Save changes</gaia-button>
      </n-flex>
    </template>
  </gaia-drawer>
</template>
